import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Target, Plus, Trash2, X, Save, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const CommunityProjectsManager = ({ token }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    goal_type: "general",
    target_value: ""
  });

  const goalTypes = [
    { value: "general", label: "General" },
    { value: "fundraising", label: "Fundraising" },
    { value: "event", label: "Event" },
    { value: "conservation", label: "Conservation" }
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/community/projects`);
      setProjects(res.data);
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        target_value: formData.target_value ? parseFloat(formData.target_value) : null
      };

      if (editingId) {
        await axios.put(`${API}/admin/community/projects/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Project updated!");
      } else {
        await axios.post(`${API}/admin/community/projects`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Project created!");
      }
      
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save project");
    }
  };

  const handleEdit = (project) => {
    setFormData({
      title: project.title,
      description: project.description,
      goal_type: project.goal_type,
      target_value: project.target_value || ""
    });
    setEditingId(project.id);
    setShowForm(true);
  };

  const handleUpdateProgress = async (projectId, newValue) => {
    try {
      await axios.put(`${API}/admin/community/projects/${projectId}`, {
        current_value: parseFloat(newValue)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Progress updated!");
      fetchProjects();
    } catch (error) {
      toast.error("Failed to update progress");
    }
  };

  const handleDelete = async (projectId) => {
    if (!confirm("Delete this project?")) return;
    
    try {
      await axios.delete(`${API}/admin/community/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Project deleted");
      fetchProjects();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await axios.put(`${API}/admin/community/projects/${projectId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status updated!");
      fetchProjects();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const resetForm = () => {
    setFormData({ title: "", description: "", goal_type: "general", target_value: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-green-100 text-green-700";
      case "completed": return "bg-blue-100 text-blue-700";
      case "paused": return "bg-yellow-100 text-yellow-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Target className="w-5 h-5 text-cyan-600" />
          Community Projects
        </CardTitle>
        <Button onClick={() => setShowForm(!showForm)} size="sm">
          {showForm ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
          {showForm ? "Cancel" : "New Project"}
        </Button>
      </CardHeader>
      <CardContent>
        {/* Create/Edit Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-cyan-50 rounded-lg space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Reef Cleanup Initiative"
                  required
                />
              </div>
              <div>
                <Label>Type</Label>
                <select
                  value={formData.goal_type}
                  onChange={(e) => setFormData({...formData, goal_type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  {goalTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe the project goal..."
                className="w-full px-3 py-2 border rounded-md min-h-[80px]"
                required
              />
            </div>
            <div>
              <Label>Target Value (optional - for progress tracking)</Label>
              <Input
                type="number"
                value={formData.target_value}
                onChange={(e) => setFormData({...formData, target_value: e.target.value})}
                placeholder="e.g., 1000 (for $1000 goal)"
              />
            </div>
            <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600">
              <Save className="w-4 h-4 mr-2" />
              {editingId ? "Update" : "Create"} Project
            </Button>
          </form>
        )}

        {/* Projects List */}
        {loading ? (
          <p className="text-center py-8 text-gray-500">Loading projects...</p>
        ) : projects.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No projects yet</p>
        ) : (
          <div className="space-y-4">
            {projects.map((project) => (
              <div key={project.id} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold">{project.title}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                        {project.goal_type}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{project.description}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(project)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(project.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Progress Section */}
                {project.target_value && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Progress: {project.current_value || 0} / {project.target_value}</span>
                      <span>{Math.round(((project.current_value || 0) / project.target_value) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-cyan-500 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(((project.current_value || 0) / project.target_value) * 100, 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        defaultValue={project.current_value || 0}
                        className="w-24 h-8 text-sm"
                        onBlur={(e) => handleUpdateProgress(project.id, e.target.value)}
                      />
                      <span className="text-xs text-gray-500">Update progress</span>
                    </div>
                  </div>
                )}

                {/* Status Controls */}
                <div className="mt-3 flex gap-2">
                  <select
                    value={project.status}
                    onChange={(e) => handleStatusChange(project.id, e.target.value)}
                    className="text-sm px-2 py-1 border rounded"
                  >
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="paused">Paused</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CommunityProjectsManager;
